import { EmployeeStatus } from './person-status';
import { EmployeeEarning } from './employee-earning';
import { EmployeeDeduction } from './employee-deductions';
import { EmployeeTax, EmployeeTaxExemptions, EmployeeTaxes } from './employee-tax';
import { EmployeeDirectDeposit } from './employee-direct-deposit';
import { EmergencyContact } from './emergency-contact';
import { EmployeeLaborAllocation } from './labor-allocation';
import { ErrorDetail } from './error-detail';
import { UntypedFormGroup } from '@angular/forms';
import { Division } from './division';
import { CompanyLocationConfig } from './company-location-config';
import { CompanyDeductionConfig } from './company-deduction-config';
import { CompanyEarningConfig } from './company-earning-config';
import { KeyPair } from './base';
import { CompanyLocalTaxConfig, CompanyTaxCodeConfig } from './tax-config';
import { PTOConfig } from './paid-time-off';
import { WorkersCompCode, WorkersCompLimit } from '../workers-comp';

export class UserEmployerModel {
  UserId: string;
  PersonId: number;
  ClientCode: string;
  CompanyDBA: string;
  CompanyName: string;
  EmploymentTypeId: number;
  EmployeeId: number | null;
}

export class UserToPersonMapModel {
  ClientCode: string;
  UserId: string;
  PersonId: number;
  Nickname: string;

  constructor() {}
}

export class UserTimeclockMapping {
  ClientCode: string;
  UserId: string;
  TimekeepingID: string;
  ServiceID: number;
  ServiceName: string;
}

export class CreatePerson {
  Person: Person;
  Taxes: EmployeeTaxes;

  constructor(person: Person, taxes: EmployeeTaxes) {
    this.Person = person;
    this.Taxes = taxes;
  }
}

export class FullPerson {
  Person: Person;

  // collections
  Deductions: EmployeeDeduction[] = [];
  DirectDeposits: EmployeeDirectDeposit[] = [];
  Earnings: EmployeeEarning[] = [];
  EmergencyContacts: EmergencyContact[] = [];
  LaborAllocations: EmployeeLaborAllocation[] = [];
  Statuses: EmployeeStatus[] = [];
  Taxes: EmployeeTaxes = new EmployeeTaxes();
  TaxExemptions: EmployeeTaxExemptions;

  constructor(
    person: Person,
    deductions: EmployeeDeduction[],
    directDeposits: EmployeeDirectDeposit[],
    earnings: EmployeeEarning[],
    emergencyContacts: EmergencyContact[],
    laborAllocations: EmployeeLaborAllocation[],
    statuses: EmployeeStatus[],
    taxes: EmployeeTaxes = new EmployeeTaxes(),
    taxExemptions: EmployeeTaxExemptions
  ) {
    this.Person = person;
    this.Deductions = deductions;
    this.DirectDeposits = directDeposits;
    this.Earnings = earnings;
    this.EmergencyContacts = emergencyContacts;
    this.LaborAllocations = laborAllocations;
    this.Statuses = statuses;
    this.Taxes = taxes;
    this.TaxExemptions = taxExemptions;
  }
}

export class Person {
  public constructor(init?: Partial<Person>) {
    Object.assign(this, init);
  }

  Id: number; // unique database identifier

  // demographic attributes
  EmploymentTypeId: number;
  UseEIN: boolean;
  FederalIdentifier: string; // ssn or ein
  IsCompany: boolean;
  FirstName: string;
  MiddleName: string;
  LastName: string;
  Birthday: Date;
  PersonalEmailAddress: string;
  HomePhoneNumber: string;
  MobilePhoneNumber: string;
  Gender: string;
  MaritalStatus: number;
  Race: number;
  StreetAddress: string;
  StreetAddress2: string;
  City: string;
  State: string;
  PostalCode: string;
  PostalCodeExtension: string;
  Country: string;
  IsNonUSAddress: boolean;
  IntegrityHash: string;

  // job details
  EmployeeId: number;
  HireDate: Date;
  ShouldReportAsNewHire: boolean;
  OKToAutoPay: boolean;
  WorksIn: string;
  CalendarId: number;
  PayFrequency: number;
  Location: number;
  Division: number;
  Department: number;
  JobTitle: string;
  IsSeasonal: boolean;
  IsOwnerOfficer: boolean;
  FullTimePartTime: string;
  SOCCode: string;
  OccupationId: string;
  BasisOfPayment: string;
  WorkEmailAddress: string;
  WorkPhoneNumber: string;
  OriginalHireDate: Date;
  CurrentStatus: EmployeeStatus;

  // workers comp
  IncludeNonW2InWorkersComp: boolean;
  WorkersCompCode: string; // null if no WC, or no codes used
  WorkersCompOwnerStatus: number;
  WorkersCompLimitId: number;

  HasPickupOrSuccessorFigures: boolean;
  LastPaid: Date;
  InvalidToPay: boolean;
  InvalidToPayDetails: ErrorDetail[]; // field names of [Taxes, WorkersComp]
}
export class PersonEditorCompanyConfig {
  Deductions: CompanyDeductionConfig[];
  Divisions: Division[] = [];
  Earnings: CompanyEarningConfig[];
  JobCodes: KeyPair[] = [];
  LocalTaxs: CompanyLocalTaxConfig[] = [];
  Locations: CompanyLocationConfig[] = [];
  PTOs: PTOConfig[];
  TaxCodes: CompanyTaxCodeConfig[];
  WorkersCompCodes: WorkersCompCode[];
  WorkersCompLimits: WorkersCompLimit[];
}

export class PersonEditorEnums {
  BasisOfPayments = [];
  BenefitTypes = [];
  EarningTypes = [];
  EmployeeStatuses = [];
  EmployeeStatusReasons = [];
  FutaReasons = [];
  Genders = [];
  MaritalStatuses = [];
  Occupations = [];
  OrganizationTypes = [];
  Races = [];
  Soccodes = [];
  States = [];
}

export class PersonEditorForms {
  PersonalDetails: UntypedFormGroup;
  JobDetails: UntypedFormGroup;
  EarningsAndDeductions: UntypedFormGroup;
  Taxes: UntypedFormGroup;
  Benefits: UntypedFormGroup;
  DirectDeposit: UntypedFormGroup;
  AuditLog: UntypedFormGroup;
}
